/**
 * Editor для doc.settlement_inventories (Фаза 2.1 #4).
 * From-registers + items table з confirmed_amount edit + variance.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { settlementInventories } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { extractApiError } from '@/utils/extractApiError';
import DatePopover from '@/components/DatePopover';
import RequiredLabel from '@/components/RequiredLabel';

const PERMISSION = 'documents.settlement_inventories.create';

interface FormState {
  organization_id: string;
  document_date: string;
  inventory_date: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_date: new Date().toISOString().slice(0, 16),
  inventory_date: new Date().toISOString().slice(0, 10),
  comment: '',
};

const STATE_LABEL: Record<string, string> = {
  IN_PROGRESS: 'У процесі', COMPLETED: 'Завершено',
};

export default function SettlementInventoryEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['settlement-inventory', id],
    queryFn: () => settlementInventories.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_date: d.document_date.slice(0, 16),
        inventory_date: d.inventory_date,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const fromRegisters = useMutation({
    mutationFn: () => settlementInventories.fromRegisters({
      organization_id: form.organization_id,
      inventory_date: form.inventory_date,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['settlement-inventories'] });
      navigate(`/settlements/settlement-inventories/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const updateItem = useMutation({
    mutationFn: (p: { itemId: string; confirmedAmount: string; reason?: string }) =>
      settlementInventories.updateItem(id!, p.itemId, {
        confirmed_amount: p.confirmedAmount,
        variance_reason: p.reason || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settlement-inventory', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const postDoc = useMutation({
    mutationFn: () => settlementInventories.post(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settlement-inventory', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова інвентаризація розрахунків' : `Інвентаризація ${docQuery.data?.document_number}`}
          {!isNew && (
            <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
              {STATE_LABEL[docQuery.data?.state ?? 'IN_PROGRESS']}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/settlements/settlement-inventories')}>Назад</button>
          {isNew && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={() => fromRegisters.mutate()} disabled={fromRegisters.isPending}>
                Згенерувати з реєстрів
              </button>
            </RequirePermission>
          )}
          {!isNew && !isPosted && (
            <button className="primary" onClick={() => postDoc.mutate()}>Провести</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Юрособа</RequiredLabel>
          {isNew ? (
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
              ))}
            </select>
          ) : (
            <div>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? form.organization_id}</div>
          )}
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date}
            disabled={!isNew} onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field">
          <label>Дата інвентаризації</label>
          <DatePopover value={form.inventory_date}
            disabled={!isNew} onChange={(v) => setForm({ ...form, inventory_date: v })} />
        </div>
      </div>

      {!isNew && docQuery.data && (
        <div style={{ marginTop: '1rem' }}>
          <h3>Items ({docQuery.data.items.length})</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th>#</th>
                <th>Контрагент</th>
                <th>Договір</th>
                <th>Книжкова</th>
                <th>Підтверджено</th>
                <th>Розбіжність</th>
                <th>Причина</th>
              </tr>
            </thead>
            <tbody>
              {docQuery.data.items.map((it) => (
                <tr key={it.id}>
                  <td>{it.line_number}</td>
                  <td style={{ fontSize: '0.75rem' }}>{it.contractor_id.slice(0, 8)}…</td>
                  <td style={{ fontSize: '0.75rem' }}>{it.contract_id?.slice(0, 8) ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{it.book_amount}</td>
                  <td>
                    <input
                      type="number" step="0.0001"
                      defaultValue={it.confirmed_amount ?? ''}
                      disabled={isPosted}
                      onBlur={(e) => {
                        if (e.target.value && e.target.value !== it.confirmed_amount) {
                          updateItem.mutate({ itemId: it.id, confirmedAmount: e.target.value });
                        }
                      }}
                      style={{ width: '100px', textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ textAlign: 'right',
                    color: parseFloat(it.variance_amount) === 0 ? '#15803d' : '#b91c1c' }}>
                    {it.variance_amount}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>{it.variance_reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f8fafc', fontWeight: 'bold' }}>
                <td colSpan={3}>Всього</td>
                <td style={{ textAlign: 'right' }}>{docQuery.data.total_book_amount}</td>
                <td style={{ textAlign: 'right' }}>{docQuery.data.total_confirmed_amount}</td>
                <td style={{ textAlign: 'right',
                  color: parseFloat(docQuery.data.total_variance_amount) === 0 ? '#15803d' : '#b91c1c' }}>
                  {docQuery.data.total_variance_amount}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {error && <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>{error}</div>}
    </div>
  );
}
